/* eslint-disable indent */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Card, IconButton, Tooltip, useMediaQuery, styled, tooltipClasses, Typography } from '@mui/material'
import { Column, useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { IconCheck, IconDots, IconDownload, IconExternalLink, IconQuestionMark, IconRefresh, IconX } from '@tabler/icons-react'
import { TableApiV2, TableHeaderApi, ContentTitle, FilterButton, Dropdown } from '~/components'
import { SearchQueysData, useDebounce, useQueryString, useToggle } from '~/hooks'
import { addHours, clearString, formatDate, formatMoney, monthNameByMonthNumber, theme, Unarray } from '~/utils'
import { EInvoiceStatus, InvoiceListQuery, PaymentModel, useInvoiceListQuery, useUpdateInvoicesToExternalNegotiationMutation, } from '~/graphql/types'
import { FinancialStatus } from '~/components/FinancialStatus'
import { FilterInvoiceDrawer } from './components/FilterInvoiceDrawer'
import { StyledA } from './styles'
import { InvoiceCancelDialog, InvoiceReportModal, MassInvoiceToExternalNegotiationDialog, SecondInvoiceGenerateModal, InvoiceExternalNegotiationDialog } from './components'
import { Button } from '~/components/Form/Button'
import { toast } from 'react-toastify'

const StyledTable = styled(Box)`
overflow-x: auto;
width: 100%;
position: relative;

.MuiTableCell-root {
  min-width: 125px;
}

/* Garante que dropdowns não sejam cortados */
.MuiTableContainer-root {
  overflow: visible;
}
`

export const InvoicesList: React.FC = () => {
  const { isTrue: drawerIsOpen, toggle: toggleDrawer, setIsTrue: setDrawerIsOpen } = useToggle(false)
  const { isTrue: updateInvoicesModalIsOpen, toggle: toggleUpdateInvoicesModal } = useToggle()
  const { isTrue: isVisibleInvoiceCancelModal, toggle: toggleInvoiceCancelModal, setIsTrue: setIsVisibleInvoiceCancelModal } = useToggle()
  const { isTrue: isVisibleInvoiceReportModal, toggle: toggleInvoiceReportModal } = useToggle()
  const { isTrue: invoiceModalIsOpen, setIsTrue: setModalInvoiceIsOpen } = useToggle(false)
  const { isTrue: isVisibleExternalNegotiationModal, toggle: toggleExternalNegotiationModal } = useToggle()
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const queryKeys: SearchQueysData[] = [
    { key: 'status', defaultValue: [], parseValue: true },
    { key: 'customerEmail', defaultValue: '' },
    { key: 'customerName', defaultValue: '' },
    { key: 'customerDocument', defaultValue: '' },
    { key: 'contractId', defaultValue: '' },
    { key: 'invoiceId', defaultValue: '' },
    { key: 'begin', defaultValue: '' },
    { key: 'end', defaultValue: '' },
    { key: 'paymentTypes', defaultValue: [], parseValue: true },
  ]
  const { searchQuerys, paginationQuerySize, searchParams, setSearchParams, page, size } = useQueryString()

  const queryValues = searchQuerys(queryKeys)
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [filterName, setFilterName] = useState(queryValues.name)

  const debouncedFilterName = useDebounce(filterName, 1000)

  useEffect(() => {
    setSearchParams(state => {
      if (debouncedFilterName) {
        state.set('name', debouncedFilterName)
        state.set('page', '0')
      } else {
        state.delete('name')
      }
      return state
    })
  }, [debouncedFilterName])

  const [updateInvoices, { loading: updateInvoicesLoading }] = useUpdateInvoicesToExternalNegotiationMutation({
    onCompleted(data) {
      if (data?.updateInvoicesToExternalNegotiation.updated && data?.updateInvoicesToExternalNegotiation?.updated[0]) {
        toast.success('Fatura atualizada com sucesso.')
      }
      if (data.updateInvoicesToExternalNegotiation.notUpdated[0]) {
        toast.error(data?.updateInvoicesToExternalNegotiation?.notUpdated[0]?.error || 'Erro ao atualizar fatura.')
      }
    },
    onError() {
      toast.error('Erro ao atualizar fatura.')
    },
  })

  const onUpdateInvoice = useCallback(async (invoiceId: string) => {
    await updateInvoices({
      variables: {
        invoiceIds: [invoiceId]
      }
    })
    await refetch()
  }, [])

  const handleExternalNegotiationClick = useCallback((invoiceId: string) => {
    setSelectedInvoiceId(invoiceId)
    toggleExternalNegotiationModal()
  }, [])

  const handleConfirmExternalNegotiation = useCallback(async () => {
    if (selectedInvoiceId) {
      await onUpdateInvoice(selectedInvoiceId)
      toggleExternalNegotiationModal()
    }
  }, [selectedInvoiceId, onUpdateInvoice])

  const { data, loading, refetch } = useInvoiceListQuery({
    variables: {
      params: {
        pageSize: size,
        pageNumber: page,
        sort: {
          field: 'dueDate',
          order: -1
        },
        ...queryValues.customerDocument?.length > 0 ? { customerDocument: clearString(queryValues.customerDocument) } : {},
        ...queryValues.customerEmail?.length > 0 ? { customerEmail: queryValues.customerEmail.trim() } : {},
        ...queryValues.customerName?.length > 0 ? { customerName: queryValues.customerName.trim() } : {},
        ...queryValues.contractId?.length > 0 ? { contractId: queryValues.contractId.trim() } : {},
        ...queryValues.invoiceId?.length > 0 ? { invoiceId: queryValues.invoiceId.trim() } : {},
        ...queryValues.status?.length > 0 ? { inStatus: queryValues.status } : {},
        ...queryValues.status?.includes('paid') && queryValues.paymentTypes?.length > 0 ? { paymentTypes: queryValues.paymentTypes } : {},

        ...queryValues?.begin && queryValues?.end ?
          {
            dueDate:
            {
              startDate: new Date(queryValues.begin),
              endDate: new Date(`${queryValues.end}Z23:59:59`)
            }
          }
          : {},
      }
    },
    fetchPolicy: 'no-cache',
    onCompleted(result) {
      setSearchParams(state => {
        state.set('items', String(result.invoiceList.meta.documentCounts))
        return state
      })
    }
  })

  const selectedInvoice: Unarray<InvoiceListQuery['invoiceList']['data']> | null = data?.invoiceList.data.find(item => item._id === selectedInvoiceId) || null

  const columns = useMemo((): Column[] => {
    return [
      {
        Header: 'Cliente',
        accessor: 'customer.name',
        Cell: (props) => <>{props.value}</>
      },
      {
        Header: 'Referência',
        accessor: 'energyBill',
        Cell: (props) => <>{monthNameByMonthNumber(props.value.reference.month)}, {Number(props.value.reference.year) + 2000}</>
      },
      {
        Header: 'Data de criação',
        accessor: 'createdAt',
        Cell: (props) => <>{formatDate(props.value)}</>,

      },
      {
        Header: 'Vencimento',
        accessor: 'dueDate',
        Cell: (props) => {

          const newDate = addHours(new Date(props.value), 3)
          return <>{formatDate(newDate)}</>

        }
      },
      {
        Header: 'Pagamento',
        accessor: 'payment',
        Cell: (props) => {
          const itemPaidAt = props.value.find((item: PaymentModel) => item.paidAt !== null)?.paidAt
          return (
            <> {itemPaidAt ? formatDate(itemPaidAt) : '-'}</>
          )
        }
      },
      {
        Header: 'Valor',
        accessor: 'amountCents',
        Cell: (props) => <>{formatMoney(props.value / 100) || '-'}</>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: ({ value, row }: any) => {
          const paymentType = row.original?.paymentType
          return (
            <FinancialStatus status={value} paymentType={paymentType} />
          )
        }
      },
      {
        Header: 'Ações',
        accessor: '_id',
        Cell: ({ value, row }: any) => {
          const isCanceled = row.original.status === EInvoiceStatus.canceled
          const isLastRow = row.index === size - 1 || row.index === size - 2

          const isEnableToUpdateInvoice = !(row.original.status === EInvoiceStatus.externalNegotiation) && row.original.status === EInvoiceStatus.late || row.original.status === EInvoiceStatus.expired
          const isExpiredInvoice = row.original.status === EInvoiceStatus.expired

          const isEnableToCancelInvoice = row.original.status !== EInvoiceStatus.canceled && row.original.status !== EInvoiceStatus.paid && row.original.status !== EInvoiceStatus.generatePDF && row.original.status !== EInvoiceStatus.requested && !(row.original.status === EInvoiceStatus.externalNegotiation)
          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {isCanceled ? (
                <Tooltip title='Conferir motivo de cancelamento' placement='bottom-start'>
                  <IconButton onClick={() => {
                    setSelectedInvoiceId(value)
                    toggleInvoiceCancelModal()
                  }} sx={{ '&:hover': { backgroundColor: '#3BA1FF20' } }}>
                    <IconQuestionMark />
                  </IconButton>
                </Tooltip>
              ) : (
                <Dropdown
                  $minWidht='200px'
                  $options={{ left: '-122px', top: isLastRow ? '-130px' : 'unset' }}
                  elementClick={
                    <Tooltip
                      title='Ver lista de ações com a fatura'
                      placement='bottom-start'
                      slotProps={{
                        popper: {
                          sx: {
                            [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
                              marginTop: '3px',
                            },
                          },
                        },
                      }}
                    >
                      <IconButton><IconDots /></IconButton>
                    </Tooltip>
                  }
                  $usePortal={size <= 4}
                >

                  <Card sx={{ width: '100%', paddingY: '1rem', display: 'flex', flexFlow: 'column', gap: '.4rem', zIndex: '999' }}>
                    <StyledA href={`${import.meta.env.VITE_APP_REST_API_URL}customer-area/download/invoice/${value}`} target='_blank' rel='noreferrer'>
                      <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%', gap: '6px', padding: '.6rem 1rem', transition: 'all .2s ease-in-out', '&:hover': { background: '#00000010' } }}>
                        <IconDownload color='#000' />
                        <Typography variant='body2' color='black.light'>
                          Baixar fatura
                        </Typography>
                      </Box>
                    </StyledA>
                    {isExpiredInvoice &&
                      (
                        <Tooltip title='Gerar 2ª via da fatura' placement='bottom'>
                          <Box
                            sx={{
                              cursor: isEnableToCancelInvoice ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              gap: '6px',
                              padding: '.6rem 1rem',
                              transition: 'all .2s ease-in-out',
                              '&:hover': {
                                background: isEnableToCancelInvoice ? '#00000010' : 'none',
                              },
                              opacity: isEnableToCancelInvoice ? 1 : 0.5,
                            }}
                            onClick={() => (setSelectedInvoiceId(value), setModalInvoiceIsOpen(true))}>

                            <IconRefresh />

                            <Typography variant='body2' color='black.light'>
                              Gerar 2ª via da fatura
                            </Typography>
                          </Box>

                        </Tooltip>
                      )
                    }
                    {(isExpiredInvoice ||
                      row.original.status === EInvoiceStatus.late) && (
                        <Box onClick={() => isEnableToUpdateInvoice && handleExternalNegotiationClick(value)}
                          sx={{
                            cursor: isEnableToUpdateInvoice ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: '6px',
                            padding: '.6rem 1rem',
                            transition: 'all .2s ease-in-out', '&:hover': { background: '#00000010' }
                          }}
                        >
                          <IconExternalLink color={isEnableToUpdateInvoice ? '#000' : theme.palette.grey[400]} />
                          <Typography variant='body2' color={isEnableToUpdateInvoice ? 'black.light' : theme.palette.grey[400]}>
                            Negociação externa
                          </Typography>
                        </Box>
                      )
                    }

                    {
                      row.original.status === EInvoiceStatus.externalNegotiation && (
                        <Box onClick={() => {
                          setSelectedInvoiceId(value)
                          toggleInvoiceReportModal()
                        }}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: '6px',
                            padding: '.6rem 1rem',
                            transition: 'all .2s ease-in-out', '&:hover': { background: '#00000010' }
                          }}
                        >
                          <IconCheck color={theme.palette.success.main} />
                          <Typography variant='body2' color={'black.light'}>
                            Marcar como pago
                          </Typography>
                        </Box>
                      )
                    }

                    <Tooltip
                      title={isEnableToCancelInvoice ? '' : 'Essa fatura não pode ser cancelada'}
                      placement='bottom'
                    >
                      <Box
                        onClick={() => {
                          if (isEnableToCancelInvoice) {
                            setSelectedInvoiceId(value)
                            toggleInvoiceCancelModal()
                          }
                        }}
                        sx={{
                          cursor: isEnableToCancelInvoice ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: '6px',
                          padding: '.6rem 1rem',
                          transition: 'all .2s ease-in-out',
                          '&:hover': {
                            background: isEnableToCancelInvoice ? '#00000010' : 'none',
                          },
                          opacity: isEnableToCancelInvoice ? 1 : 0.5,
                        }}
                      >
                        <IconX color={isEnableToCancelInvoice ? '#EF4444' : '#B0B0B0'} />
                        <Typography variant='body2' color='black.light'>
                          Cancelar fatura
                        </Typography>
                      </Box>
                    </Tooltip>

                  </Card >
                </Dropdown >
              )}

            </Box >
          )
        }

      },
    ]
  }, [size])

  const dataWithMemo = useMemo(() => data?.invoiceList.data || [], [data])

  // const defaultColumnHiddens = isLowerlg ? ['createdAt', 'payment.paidAt'].concat(isLowerMd ? ['referenceOrder'] : []).concat(isLowerSm ? ['dueDate'] : '') : []

  const tableMethods = useTable({
    columns,
    data: dataWithMemo,
    defaultColumn: {
      Filter: <></>
    },
    manualPagination: true,
    initialState: {
      // hiddenColumns: defaultColumnHiddens
    }
  }, useFilters, useSortBy, usePagination, useRowSelect
  )

  const reloadInvoiceList = () => {
    refetch()
  }

  return (
    <div>
      <Card sx={{ background: 'white', padding: '0 1rem', borderRadius: '1rem' }}>
        <ContentTitle
          title={'Faturas'}
          description={'Visualize e gerencie todas as faturas geradas'}
          breadcrumbLinks={{ currentLink: 'Faturas', links: [{ href: '/app/financials', label: 'Financeiro' }] }}
          rightContent={
            <Box>
              <Button onClick={toggleUpdateInvoicesModal}>Atualizar faturas</Button>
            </Box>
          }
        />
        <TableHeaderApi
          startElement={
            (
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem', width: '100%',
                flexFlow: isLowerSm ? 'column-reverse' : 'row'
              }}>
                {/* <TextField
                  fullWidth
                  sx={{ maxWidth: '400px' }}
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder='Buscar por nome, documento E-mail do cliente'
                  InputProps={{
                    endAdornment: (
                      <>
                        <InputAdornment position='end'>
                          <IconSearch />
                        </InputAdornment>
                      </>
                    )
                  }}
                /> */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <FilterButton onCustomClear={() => setFilterName('')} filtersToClear={queryKeys} toggleDrawer={toggleDrawer} filterCounter={searchParams.size - paginationQuerySize} />
                </Box>

                <FilterInvoiceDrawer queryKeys={queryKeys} drawerIsOpen={drawerIsOpen} setDrawerIsOpen={setDrawerIsOpen} toggleDrawer={toggleDrawer} />
              </Box>
            )
          }
        />
        <StyledTable>
          <TableApiV2
            isLoading={loading}
            tableMethods={tableMethods}
            columns={columns}
          />
        </StyledTable>
      </Card>
      <InvoiceCancelDialog
        isVisible={isVisibleInvoiceCancelModal}
        setIsVisibleInvoiceCancelModal={setIsVisibleInvoiceCancelModal}
        invoiceId={selectedInvoiceId}
        onInvoiceCancelSuccess={reloadInvoiceList}
        selectedInvoice={selectedInvoice}
      />
      <MassInvoiceToExternalNegotiationDialog isVisible={updateInvoicesModalIsOpen} toggleModal={toggleUpdateInvoicesModal} />
      <InvoiceReportModal isVisible={isVisibleInvoiceReportModal} toggleModal={toggleInvoiceReportModal} invoiceId={selectedInvoiceId} onInvoiceReportSuccess={reloadInvoiceList} />
      {invoiceModalIsOpen && Boolean(selectedInvoiceId) && (
        <SecondInvoiceGenerateModal
          invoiceId={selectedInvoiceId}
          contractId={selectedInvoice?.contract?.id || null}
          isVisible={invoiceModalIsOpen}
          onClose={() => setModalInvoiceIsOpen(false)}
          onSuccess={reloadInvoiceList}
        />
      )}
      <InvoiceExternalNegotiationDialog
        isVisible={isVisibleExternalNegotiationModal}
        onClose={toggleExternalNegotiationModal}
        onConfirm={handleConfirmExternalNegotiation}
        loading={updateInvoicesLoading}
      />
    </div >
  )
}
